import Modal from "../../../../../../../../components/Modal";
import FullLoading from "../../../../../../../../components/FullLoading";
import Button from "../../../../../../../../components/Button";
import { TrashIcon } from "@heroicons/react/24/outline";
import { IconDropdown } from "../../../../../../../../components/Icon";
import {
  KontrolIcon,
  mapStringToIcon,
} from "../../../../../../../../components/Icon/utils";

import { useActivityData } from "./hooks/useActivityData";
import { useActivityForm } from "./hooks/useActivityForm";
import { useActivityMutations } from "./hooks/useActivityMutations";
import {
  buildActivityPayload,
  validateEndDateTime,
} from "./hooks/useActivitySubmit";
import type { ActivityFormValues } from "./zod/activitySchema";
import type {
  ActivityFragment,
  UpdateActivityInput,
} from "../../../../../../../../__generated__/graphql";
import type { DocumentMetadata } from "./types";

// Section components
import TitleSection from "./components/TitleSection";
import ParticipantsSelectInput from "./components/ParticipantsSelectInput";
import TimeSection from "./components/TimeSection";
import ContactsSection from "./components/ContactsSection";
import DescriptionSection from "./components/DescriptionSection";
import LocationSection from "./components/LocationSection";
import DocumentsSection from "./components/DocumentsSection";
import RemindersSection from "./components/RemindersSection";

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId?: string;
  projectId?: string;
  defaultActivity?: ActivityFragment | null;
  isDuplicate?: boolean;
  onActivityCreated?: (startDate: Date) => void;
  defaultStartDate?: string; // Format: YYYY-MM-DD
  defaultStartTime?: string; // Format: HH:mm
}

export default function ActivityModal({
  isOpen,
  onClose,
  eventId,
  projectId,
  defaultActivity,
  isDuplicate = false,
  onActivityCreated,
  defaultStartDate,
  defaultStartTime,
}: ActivityModalProps) {
  // Data fetching
  const { isLoading, participantOptions, eventDetailsData, effectiveProjectId } = useActivityData({
    eventId,
    projectId,
  });

  // Form state management
  const {
    form,
    sectionVisibility,
    toggleSection,
    selectedIcon,
    setSelectedIcon,
    selectedColor,
    setSelectedColor,
    documents,
    setDocuments,
    documentErrors,
    setDocumentErrors,
    reminders,
    setReminders,
    contacts,
    setContacts,
    resetFormState,
  } = useActivityForm({
    defaultActivity,
    eventDetailsData,
    isDuplicate,
    defaultStartDate,
    defaultStartTime,
  });

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  // Mutations
  const {
    isSubmitting,
    handleCreate,
    handleUpdate,
    handleDelete,
    uploadDocuments,
  } = useActivityMutations({
    eventId,
    projectId,
    defaultActivityId: defaultActivity?.id,
    participantOptions,
    onSuccess: handleClose,
  });

  const validateDocuments = (documentsToUpload: DocumentMetadata[]) => {
    if (documentsToUpload.length === 0) {
      return true;
    }

    let isValid = true;
    const newErrors = [...documentErrors];

    documentsToUpload.forEach((doc, index) => {
      newErrors[index] = {};

      if (!doc.file || doc.title.trim() === "") {
        newErrors[index].title = "Le titre est requis";
        isValid = false;
      }

      if (!doc.file) {
        newErrors[index].file = "Un fichier est requis";
        isValid = false;
      }
    });

    setDocumentErrors(newErrors);
    return isValid;
  };

  const onSubmit = async (data: ActivityFormValues) => {
    try {
      // Validate end date/time if displayEndTime is true
      if (
        !validateEndDateTime(data, sectionVisibility.endTime, form.setError)
      ) {
        return;
      }

      const payload = await buildActivityPayload(data, {
        displayEndTime: sectionVisibility.endTime,
        displayDescription: sectionVisibility.description,
        displayLocation: sectionVisibility.location,
        displayDocuments: sectionVisibility.documents,
        displayContacts: sectionVisibility.contacts,
        documents,
        reminders,
        contacts,
        defaultActivity,
        isDuplicate,
        uploadDocuments,
        validateDocuments,
      });

      if (defaultActivity && !isDuplicate) {
        await handleUpdate(payload as UpdateActivityInput);
      } else {
        await handleCreate({ ...payload, eventId, projectId });
        // Call onActivityCreated callback with the activity start date
        if (onActivityCreated) {
          const [year, month, day] = data.startDay.split("-").map(Number);
          const startDate = new Date(year, month - 1, day);
          onActivityCreated(startDate);
        }
      }
    } catch (error) {
      console.error("Error submitting activity:", error);
      // Error handling is done in the mutation hooks
    }
  };

  const getCtaLabel = () => {
    if (defaultActivity && isDuplicate) {
      return "Dupliquer";
    } else if (defaultActivity) {
      return "Modifier";
    } else {
      return "Créer";
    }
  };

  const getTitle = () => {
    if (defaultActivity && isDuplicate) {
      return "Dupliquer l'activité";
    } else if (defaultActivity) {
      return "Modifier l'activité";
    } else {
      return "Nouvelle activité";
    }
  };

  if (isOpen && isLoading) {
    return <FullLoading />;
  }

  return (
    <Modal
      confirmClose={form.formState.isDirty}
      isModalOpen={isOpen}
      className="min-w-[90%]"
      onClose={handleClose}
      title={getTitle()}
      description="Créez toute sorte d'activités. Un train à prendre, une communication sur les réseaux sociaux, un rendez-vous important etc."
    >
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="overflow-y-auto max-h-[80vh] min-w-md custom-scrollbar"
      >
        <div className="flex gap-4">
          <div className="flex-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="text-sm">
                Choisir une icône et une couleur{" "}
                <span className="text-secondary">(optionnel)</span>
              </div>
              <IconDropdown
                color={
                  form.getValues("color") ||
                  selectedColor ||
                  "var(--color-accent)"
                }
                setColor={(color) => {
                  setSelectedColor(color);
                  form.setValue("color", color);
                }}
                icon={
                  selectedIcon
                    ? mapStringToIcon(selectedIcon)
                    : KontrolIcon.GENERAL
                }
                setIcon={(icon) => {
                  form.setValue("icon", icon);
                  setSelectedIcon(icon);
                }}
              />
            </div>

            <TitleSection
              register={form.register}
              errors={form.formState.errors}
            />

            <ParticipantsSelectInput
              control={form.control}
              errors={form.formState.errors}
              participantOptions={participantOptions}
              showGroups={!!effectiveProjectId}
              projectId={effectiveProjectId}
              setValue={form.setValue}
              currentGroups={form.watch("groupParticipants") ?? []}
              onChange={(participants) => {
                // Remove from documents the participants that are not in the participants list
                setDocuments(
                  documents.map((doc) => ({
                    ...doc,
                    selectedParticipants: doc.selectedParticipants.filter((p) =>
                      participants.some(
                        (pp) => pp.userOrganizationId === p.userOrganizationId
                      )
                    ),
                  }))
                );
              }}
            />

            <TimeSection
              register={form.register}
              errors={form.formState.errors}
              displayEndTime={sectionVisibility.endTime}
              setDisplayEndTime={(value) => toggleSection("endTime", value)}
            />
          </div>

          <div className="flex-4 flex flex-col gap-4">
            <ContactsSection
              displayContacts={sectionVisibility.contacts}
              setDisplayContacts={(displayContacts) => {
                if (!displayContacts) {
                  setContacts([]);
                }
                toggleSection("contacts", displayContacts);
              }}
              contacts={contacts}
              setContacts={setContacts}
            />

            <DescriptionSection
              register={form.register}
              errors={form.formState.errors}
              displayDescription={sectionVisibility.description}
              setDisplayDescription={(displayDescription) => {
                if (!displayDescription) {
                  form.reset({
                    ...form.getValues(),
                    description: "",
                  });
                }
                toggleSection("description", displayDescription);
              }}
            />

            <LocationSection
              displayLocation={sectionVisibility.location}
              setDisplayLocation={(displayLocation) => {
                if (!displayLocation) {
                  form.reset({
                    ...form.getValues(),
                    locationAddress: "",
                    locationCity: "",
                    locationLabel: "",
                  });
                }
                toggleSection("location", displayLocation);
              }}
              watch={form.watch}
              setValue={form.setValue}
            />

            <DocumentsSection
              displayDocuments={sectionVisibility.documents}
              setDisplayDocuments={(value) => toggleSection("documents", value)}
              documents={documents}
              setDocuments={setDocuments}
              documentErrors={documentErrors}
              setDocumentErrors={setDocumentErrors}
              participantOptions={form.watch("participants")?.map((p) => ({
                value: p.userOrganizationId,
                userOrganizationId: p.userOrganizationId,
                organizationId: p.organizationId,
                label: p.label,
              }))}
            />

            <RemindersSection
              displayReminders={sectionVisibility.reminders}
              setDisplayReminders={(value) => toggleSection("reminders", value)}
              reminders={reminders}
              setReminders={setReminders}
            />
          </div>
        </div>

        <div className="flex justify-between space-x-3 pt-4">
          {defaultActivity ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleDelete(defaultActivity.id)}
              className="flex items-center gap-2 text-red-400 hover:text-red-400/80"
            >
              <TrashIcon className="w-5 h-5 cursor-pointer flex-shrink-0" />
              Supprimer
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" isLoading={isSubmitting}>
              {getCtaLabel()}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
